import React from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  Link,
  makeStyles,
  MenuItem,
  Select,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@material-ui/core';
import { TestCaseVersionHistoryPanel } from './version-history';
import {
  createTestCase,
  exportTestCases,
  ExportFormat,
  generateTestCases,
  getActiveTestCaseTemplate,
  getRequirementForTestCaseGeneration,
  getTestCaseThread,
  getTraceability,
  isTestCaseIdFieldKey,
  saveTestCaseInstance,
  startWorking,
  submitTestCaseFeedback,
} from './api';
import Alert from '@material-ui/lab/Alert';
import {
  ActiveTestCaseTemplateField,
  GeneratedTestCaseField,
  TestCaseThread,
  TraceabilityRequirement,
  TraceabilitySummary,
} from './types';
import { TestCaseFeedbackDialog } from './feedback';
import { InstanceReviewPanel } from './instance-review';
import SearchIcon from '@material-ui/icons/Search';
import InputAdornment from '@material-ui/core/InputAdornment';
import { useTranslation } from 'react-i18next';
import type { TestCaseThreadStatus } from './types';

type TraceabilityDashboardProps = {
  projectId: string | number;
};

const useStyles = makeStyles(theme => ({
  summaryCard: {
    height: '100%',
  },
  summaryCardContent: {
    minHeight: 112,
    height: '100%',
    boxSizing: 'border-box',
  },
  coverageCard: {
    height: '100%',
    backgroundColor: '#f4f8ff',
  },
  tableHeader: {
    textAlign: 'center',
    verticalAlign: 'middle',
  },
  actionCell: {
    textAlign: 'center',
    whiteSpace: 'nowrap',
  },
  actionButton: {
    minWidth: 88,
    whiteSpace: 'nowrap',
  },
  searchField: {
    width: 400,

    [theme.breakpoints.down('xs')]: {
      width: '100%',
    },
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    flexWrap: 'wrap',
    marginBottom: theme.spacing(2),
  },
  toolbarActions: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
  },
  exportFormat: {
    minWidth: 150,
  },
}));

const MAX_EXPORT_TEST_CASES = 50;

export const TraceabilityDashboard = ({
  projectId,
}: TraceabilityDashboardProps) => {
  const { t } = useTranslation('testCases');
  const classes = useStyles();

  // ---- CB25SES-1031: TC creation form ----
  const [createRequirement, setCreateRequirement] =
    React.useState<TraceabilityRequirement | null>(null);
  const [createValues, setCreateValues] = React.useState<
    Record<string, string>
  >({});

  const [templateFields, setTemplateFields] = React.useState<
    ActiveTestCaseTemplateField[]
  >([]);
  const [createRequirementText, setCreateRequirementText] = React.useState('');
  const [templateLoading, setTemplateLoading] = React.useState(false);
  const createLoadRequestIdRef = React.useRef(0);

  const editableTemplateFields = React.useMemo(
    () =>
      templateFields.filter(
        field => !field.readOnly && !isTestCaseIdFieldKey(field.key),
      ),
    [templateFields],
  );

  const hasCreateInput = Object.values(createValues).some(value =>
    value.trim(),
  );

  const requiredCreateFieldsComplete = editableTemplateFields.every(
    field => !field.required || Boolean((createValues[field.key] ?? '').trim()),
  );

  const handleCreateFieldChange = (key: string, value: string) => {
    setCreateValues(previous => ({ ...previous, [key]: value }));
  };

  const [creating, setCreating] = React.useState(false);
  const [createError, setCreateError] = React.useState<string | null>(null);

  const handleCreateTestCase = async (requirement: TraceabilityRequirement) => {
    const requirementThreadId = requirement.requirementThreadId;

    const requestId = createLoadRequestIdRef.current + 1;

    createLoadRequestIdRef.current = requestId;

    setCreateRequirement(requirement);
    setCreateValues({});
    setCreateError(null);
    setTemplateFields([]);
    setCreateRequirementText('');

    if (!requirementThreadId) {
      setCreateError(
        t('testCaseGeneration.createForm.missingRequirementThreadId'),
      );

      return;
    }

    setTemplateLoading(true);

    try {
      const [templateResult, requirementResult] = await Promise.allSettled([
        getActiveTestCaseTemplate(projectId),
        getRequirementForTestCaseGeneration(requirementThreadId),
      ]);

      if (createLoadRequestIdRef.current !== requestId) {
        return;
      }

      if (templateResult.status !== 'fulfilled') {
        throw templateResult.reason;
      }

      setTemplateFields(templateResult.value.fields);

      if (requirementResult.status === 'fulfilled') {
        setCreateRequirementText(requirementResult.value.text);
      } else {
        setCreateRequirementText('');
      }
    } catch {
      if (createLoadRequestIdRef.current !== requestId) {
        return;
      }

      setTemplateFields([]);
      setCreateRequirementText('');

      setCreateError(t('testCaseGeneration.createForm.templateLoadFailed'));
    } finally {
      if (createLoadRequestIdRef.current === requestId) {
        setTemplateLoading(false);
      }
    }
  };

  const handleCloseCreateForm = () => {
    if (creating || templateLoading) {
      return;
    }

    if (hasCreateInput) {
      // eslint-disable-next-line no-alert
      const confirmed = window.confirm(
        t('testCaseGeneration.createForm.discardConfirm'),
      );

      if (!confirmed) {
        return;
      }
    }

    createLoadRequestIdRef.current += 1;

    setCreateRequirement(null);
    setCreateValues({});
    setTemplateFields([]);
    setCreateRequirementText('');
    setCreateError(null);
    setTemplateLoading(false);
  };

  const [detailOpen, setDetailOpen] = React.useState(false);
  const [detailRequirement, setDetailRequirement] =
    React.useState<TraceabilityRequirement | null>(null);
  const [detailThread, setDetailThread] = React.useState<TestCaseThread | null>(
    null,
  );
  const [detailLoading, setDetailLoading] = React.useState(false);
  const [detailError, setDetailError] = React.useState<string | null>(null);
  const [detailActionError, setDetailActionError] = React.useState<
    string | null
  >(null);
  const [detailBusy, setDetailBusy] = React.useState(false);
  const [editValues, setEditValues] = React.useState<Record<string, string>>(
    {},
  );

  const [selectedTraceabilityIds, setSelectedTraceabilityIds] = React.useState<
    Set<string>
  >(new Set());

  const [traceabilityExportFormat, setTraceabilityExportFormat] =
    React.useState<ExportFormat>('markdown');

  const [exportingTraceability, setExportingTraceability] =
    React.useState(false);

  const [traceabilityExportSuccessOpen, setTraceabilityExportSuccessOpen] =
    React.useState(false);

  const [traceabilityExportErrorOpen, setTraceabilityExportErrorOpen] =
    React.useState(false);

  const [
    traceabilityExportValidationMessage,
    setTraceabilityExportValidationMessage,
  ] = React.useState<string | null>(null);

  const [generatingRequirementId, setGeneratingRequirementId] = React.useState<
    string | null
  >(null);

  const [generateSuccessOpen, setGenerateSuccessOpen] = React.useState(false);

  const [generateErrorOpen, setGenerateErrorOpen] = React.useState(false);

  const getCurrentFields = (thread: TestCaseThread) => {
    const currentInstance = thread.currentInstanceId
      ? thread.instances.find(
          instance => instance.instanceId === thread.currentInstanceId,
        )
      : undefined;

    return (
      (
        currentInstance ??
        thread.instances.find(
          instance => instance.versionNumber === thread.currentVersionNumber,
        )
      )?.fields ?? []
    );
  };

  const toEditText = (value: string | string[]) =>
    Array.isArray(value) ? value.join('\n') : value;

  const applyThread = (thread: TestCaseThread) => {
    const values: Record<string, string> = {};

    getCurrentFields(thread).forEach(field => {
      values[field.key] = toEditText(field.value);
    });

    setDetailThread(thread);
    setEditValues(values);
  };

  const currentFields = detailThread ? getCurrentFields(detailThread) : [];

  const isDirty = currentFields.some(
    field => (editValues[field.key] ?? '') !== toEditText(field.value),
  );

  const handleFieldChange = (key: string, value: string) => {
    setEditValues(previous => ({ ...previous, [key]: value }));
  };

  const handleOpenTestCase = async (requirement: TraceabilityRequirement) => {
    setDetailRequirement(requirement);
    setDetailThread(null);
    setDetailError(null);
    setDetailActionError(null);
    setDetailOpen(true);

    if (!requirement.testCaseThreadId) {
      setDetailError(t('testCaseGeneration.detailView.loadFailed'));
      return;
    }

    try {
      setDetailLoading(true);
      const thread = await getTestCaseThread(requirement.testCaseThreadId);
      applyThread(thread);
    } catch {
      setDetailError(t('testCaseGeneration.detailView.loadFailed'));
    } finally {
      setDetailLoading(false);
    }
  };

  const handleStartWorking = async () => {
    if (!detailThread) {
      return;
    }

    try {
      setDetailBusy(true);
      setDetailActionError(null);
      const thread = await startWorking(detailThread.threadId);
      applyThread(thread);
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      await loadTraceability();
    } catch {
      setDetailActionError(t('testCaseGeneration.detailView.startFailed'));
    } finally {
      setDetailBusy(false);
    }
  };

  const handleSaveEdit = async () => {
    if (!detailThread || !isDirty) {
      return;
    }

    const updatedFields = currentFields
      .filter(field => !isTestCaseIdFieldKey(field.key))
      .map(field => {
        const text = editValues[field.key] ?? '';

        return {
          ...field,
          value: Array.isArray(field.value)
            ? text
                .split('\n')
                .map(line => line.trim())
                .filter(Boolean)
            : text.trim(),
        };
      });

    try {
      setDetailBusy(true);
      setDetailActionError(null);
      const thread = await saveTestCaseInstance(
        detailThread.threadId,
        updatedFields,
      );
      applyThread(thread);
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      await loadTraceability();
    } catch {
      setDetailActionError(t('testCaseGeneration.detailView.saveFailed'));
    } finally {
      setDetailBusy(false);
    }
  };

  const handleCloseDetail = () => {
    if (detailBusy) {
      return;
    }

    if (isDirty) {
      // eslint-disable-next-line no-alert
      const confirmed = window.confirm(
        t('testCaseGeneration.detailView.discardConfirm'),
      );

      if (!confirmed) {
        return;
      }
    }

    setDetailOpen(false);
    setDetailThread(null);
    setDetailRequirement(null);
    setDetailError(null);
    setDetailActionError(null);
    setEditValues({});
  };

  React.useEffect(() => {
    if (!detailOpen || !isDirty) {
      return undefined;
    }

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [detailOpen, isDirty]);

  const renderStatusChip = (status: string) => {
    switch (status) {
      case 'APPROVED':
        return (
          <Chip
            size="small"
            label={t('testCaseGeneration.traceability.approved')}
            style={{
              backgroundColor: '#e6f4ea',
              color: '#137333',
              fontWeight: 600,
            }}
          />
        );

      case 'DRAFT':
        return (
          <Chip
            size="small"
            label={t('testCaseGeneration.traceability.draft')}
            style={{
              backgroundColor: '#fff3cd',
              color: '#856404',
            }}
          />
        );

      default:
        return (
          <Chip
            size="small"
            label={t('testCaseGeneration.traceability.notCovered')}
            style={{
              backgroundColor: '#fdecea',
              color: '#b71c1c',
            }}
          />
        );
    }
  };

  const renderActionButton = (requirement: TraceabilityRequirement) => {
    if (!requirement.testCaseId) {
      return (
        <Button
          className={classes.actionButton}
          size="small"
          variant="outlined"
          color="primary"
          onClick={() => handleCreateTestCase(requirement)}
        >
          {t('testCaseGeneration.traceability.create')}
        </Button>
      );
    }

    if (!requirement.testCaseThreadId) {
      return (
        <Typography variant="body2" color="textSecondary">
          —
        </Typography>
      );
    }

    return (
      <Button
        className={classes.actionButton}
        size="small"
        variant="outlined"
        color="primary"
        onClick={() => handleOpenTestCase(requirement)}
      >
        {t('testCaseGeneration.traceability.open')}
      </Button>
    );
  };

  const [selectedHistoryRequirement, setSelectedHistoryRequirement] =
    React.useState<TraceabilityRequirement | null>(null);

  const [traceabilityFeedbackOpen, setTraceabilityFeedbackOpen] =
    React.useState(false);

  const [submittingTraceabilityFeedback, setSubmittingTraceabilityFeedback] =
    React.useState(false);

  const [traceabilityFeedbackSuccessOpen, setTraceabilityFeedbackSuccessOpen] =
    React.useState(false);

  const [
    traceabilityFeedbackErrorMessage,
    setTraceabilityFeedbackErrorMessage,
  ] = React.useState<string | null>(null);

  const [historyRefreshKey, setHistoryRefreshKey] = React.useState(0);

  const [instanceReviewOpen, setInstanceReviewOpen] = React.useState(false);
  const [selectedReviewRequirement, setSelectedReviewRequirement] =
    React.useState<TraceabilityRequirement | null>(null);

  const [requirements, setRequirements] = React.useState<
    TraceabilityRequirement[]
  >([]);

  const [summary, setSummary] = React.useState<TraceabilitySummary>({
    total: 0,
    approved: 0,
    draft: 0,
    notCovered: 0,
    coveragePercent: 0,
  });

  const [loading, setLoading] = React.useState(true);

  const [searchText, setSearchText] = React.useState('');

  const handleOpenInstanceReview = (requirement: TraceabilityRequirement) => {
    if (!requirement.testCaseThreadId || !requirement.testCaseId) {
      return;
    }

    setSelectedReviewRequirement(requirement);
    setInstanceReviewOpen(true);
  };

  const handleOpenVersionHistory = (requirement: TraceabilityRequirement) => {
    if (!requirement.testCaseId || !requirement.testCaseThreadId) {
      return;
    }

    setSelectedHistoryRequirement(requirement);
  };

  const loadTraceability = React.useCallback(async () => {
    try {
      setLoading(true);

      const response = await getTraceability(Number(projectId));

      setRequirements(response.requirements);
      setSummary(response.summary);

      const availableThreadIds = new Set(
        response.requirements
          .map(requirement => requirement.testCaseThreadId)
          .filter((threadId): threadId is string => Boolean(threadId)),
      );

      setSelectedTraceabilityIds(previous => {
        const next = new Set<string>();

        previous.forEach(id => {
          if (availableThreadIds.has(id)) {
            next.add(id);
          }
        });

        return next;
      });

      return response;
    } catch {
      // Existing page behavior does not expose a load Snackbar.
      return null;
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  React.useEffect(() => {
    void loadTraceability();
  }, [loadTraceability]);

  const handleSubmitTraceabilityFeedback = async (instruction: string) => {
    if (
      !selectedHistoryRequirement?.testCaseId ||
      !selectedHistoryRequirement.testCaseThreadId ||
      !instruction.trim() ||
      submittingTraceabilityFeedback
    ) {
      return;
    }

    const numericProjectId = Number(projectId);

    if (!Number.isInteger(numericProjectId) || numericProjectId <= 0) {
      setTraceabilityFeedbackErrorMessage(
        t('testCaseGeneration.feedback.failed'),
      );
      return;
    }

    try {
      setSubmittingTraceabilityFeedback(true);
      setTraceabilityFeedbackErrorMessage(null);

      await submitTestCaseFeedback(
        numericProjectId,
        selectedHistoryRequirement.testCaseThreadId,
        selectedHistoryRequirement.testCaseId,
        instruction,
      );

      setTraceabilityFeedbackOpen(false);

      setHistoryRefreshKey(previous => previous + 1);

      setTraceabilityFeedbackSuccessOpen(true);

      await loadTraceability();
    } catch (error) {
      setTraceabilityFeedbackErrorMessage(
        error instanceof Error
          ? error.message
          : t('testCaseGeneration.feedback.failed'),
      );
    } finally {
      setSubmittingTraceabilityFeedback(false);
    }
  };

  const handleSubmitCreateForm = async () => {
    if (!createRequirement) {
      return;
    }

    const requirementThreadId = createRequirement.requirementThreadId;

    if (!requirementThreadId) {
      setCreateError(t('testCaseGeneration.createForm.failed'));
      return;
    }

    const fields: GeneratedTestCaseField[] = editableTemplateFields.map(
      field => {
        const text = createValues[field.key] ?? '';

        return {
          key: field.key,
          label: field.label,

          value: field.isList
            ? text
                .split('\n')
                .map(line => line.trim())
                .filter(Boolean)
            : text.trim(),
        };
      },
    );

    try {
      setCreating(true);
      setCreateError(null);

      const createdThread = await createTestCase(requirementThreadId, fields);
      const thread = await getTestCaseThread(createdThread.threadId);

      setDetailRequirement(createRequirement);
      setDetailError(null);
      setDetailActionError(null);

      applyThread(thread);

      setDetailOpen(true);

      createLoadRequestIdRef.current += 1;

      setCreateRequirement(null);
      setCreateValues({});
      setTemplateFields([]);
      setCreateRequirementText('');
      setCreateError(null);
      setTemplateLoading(false);

      setHistoryRefreshKey(previous => previous + 1);
      await loadTraceability();
    } catch {
      setCreateError(t('testCaseGeneration.createForm.failed'));
    } finally {
      setCreating(false);
    }
  };

  const totalRequirements = summary.total;

  const approvedCount = summary.approved;

  const draftCount = summary.draft;

  const notCoveredCount = summary.notCovered;

  const coveragePercentage = Math.round(summary.coveragePercent);

  const coveredCount = approvedCount + draftCount;

  const normalizedSearchText = searchText.trim().toLowerCase();

  const filteredRequirements = requirements.filter(
    requirement =>
      requirement.id.toLowerCase().includes(normalizedSearchText) ||
      requirement.name.toLowerCase().includes(normalizedSearchText) ||
      requirement.testCaseId.toLowerCase().includes(normalizedSearchText),
  );

  const handleGenerateFromTraceability = async (
    requirement: TraceabilityRequirement,
  ) => {
    if (generatingRequirementId || requirement.status !== 'NOT_COVERED') {
      return;
    }

    try {
      setGeneratingRequirementId(requirement.id);
      setGenerateErrorOpen(false);

      const requirementContext = await getRequirementForTestCaseGeneration(
        requirement.requirementThreadId,
      );

      const response = await generateTestCases([requirementContext]);

      if (response.testCases.length === 0) {
        throw new Error('Generation response did not contain a test case.');
      }

      const refreshedTraceability = await loadTraceability();

      if (!refreshedTraceability) {
        throw new Error('Traceability reload failed after generation.');
      }

      const refreshedRequirement = refreshedTraceability.requirements.find(
        item => item.id === requirement.id,
      );

      if (
        !refreshedRequirement ||
        refreshedRequirement.status !== 'DRAFT' ||
        !refreshedRequirement.testCaseId
      ) {
        throw new Error('Traceability row was not refreshed to DRAFT.');
      }

      setGenerateSuccessOpen(true);
    } catch {
      setGenerateErrorOpen(true);
    } finally {
      setGeneratingRequirementId(null);
    }
  };

  const exportableRequirements = filteredRequirements.filter(
    requirement =>
      Boolean(requirement.testCaseId) && Boolean(requirement.testCaseThreadId),
  );

  const allExportableSelected =
    exportableRequirements.length > 0 &&
    exportableRequirements.every(requirement =>
      selectedTraceabilityIds.has(requirement.testCaseThreadId as string),
    );

  const someExportableSelected = exportableRequirements.some(requirement =>
    selectedTraceabilityIds.has(requirement.testCaseThreadId as string),
  );

  const handleToggleAllTraceability = (checked: boolean) => {
    if (checked && exportableRequirements.length > MAX_EXPORT_TEST_CASES) {
      setTraceabilityExportValidationMessage(
        t('testCaseGeneration.export.selectionLimit', {
          count: MAX_EXPORT_TEST_CASES,
        }),
      );

      return;
    }

    setSelectedTraceabilityIds(previous => {
      const next = new Set(previous);

      exportableRequirements.forEach(requirement => {
        const threadId = requirement.testCaseThreadId;

        if (!threadId) {
          return;
        }

        if (checked) {
          next.add(threadId);
        } else {
          next.delete(threadId);
        }
      });

      return next;
    });

    setTraceabilityExportValidationMessage(null);
  };

  const handleToggleTraceability = (testCaseThreadId: string) => {
    if (
      !selectedTraceabilityIds.has(testCaseThreadId) &&
      selectedTraceabilityIds.size >= MAX_EXPORT_TEST_CASES
    ) {
      setTraceabilityExportValidationMessage(
        t('testCaseGeneration.export.selectionLimit', {
          count: MAX_EXPORT_TEST_CASES,
        }),
      );

      return;
    }

    setSelectedTraceabilityIds(previous => {
      const next = new Set(previous);

      if (next.has(testCaseThreadId)) {
        next.delete(testCaseThreadId);
      } else {
        next.add(testCaseThreadId);
      }

      return next;
    });

    setTraceabilityExportValidationMessage(null);
  };

  const handleTraceabilityExport = async () => {
    const testCaseThreadIds = Array.from(selectedTraceabilityIds);

    if (testCaseThreadIds.length === 0) {
      setTraceabilityExportErrorOpen(true);
      return;
    }

    if (testCaseThreadIds.length > MAX_EXPORT_TEST_CASES) {
      setTraceabilityExportValidationMessage(
        t('testCaseGeneration.export.selectionLimit', {
          count: MAX_EXPORT_TEST_CASES,
        }),
      );

      return;
    }

    try {
      setTraceabilityExportValidationMessage(null);
      setExportingTraceability(true);
      setTraceabilityExportErrorOpen(false);

      const numericProjectId = Number(projectId);

      if (!Number.isInteger(numericProjectId) || numericProjectId <= 0) {
        throw new Error(`Invalid project ID: ${projectId}`);
      }

      const result = await exportTestCases(
        numericProjectId,
        testCaseThreadIds,
        traceabilityExportFormat,
      );

      const url = window.URL.createObjectURL(result.blob);
      const link = document.createElement('a');

      link.href = url;
      link.download = result.filename;

      document.body.appendChild(link);

      try {
        link.click();
      } finally {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }

      setTraceabilityExportSuccessOpen(true);
    } catch {
      setTraceabilityExportErrorOpen(true);
    } finally {
      setExportingTraceability(false);
    }
  };

  const renderReviewAction = (requirement: TraceabilityRequirement) => {
    if (requirement.status === 'NOT_COVERED') {
      const isGenerating = generatingRequirementId === requirement.id;

      return (
        <Button
          className={classes.actionButton}
          size="small"
          variant="contained"
          color="primary"
          disabled={generatingRequirementId !== null}
          onClick={() => handleGenerateFromTraceability(requirement)}
          startIcon={
            isGenerating ? (
              <CircularProgress size={16} color="inherit" />
            ) : undefined
          }
        >
          {isGenerating
            ? t('testCaseGeneration.traceability.generating')
            : t('testCaseGeneration.traceability.generate')}
        </Button>
      );
    }

    const canReview =
      Boolean(requirement.testCaseThreadId) &&
      Boolean(requirement.testCaseId) &&
      (requirement.status === 'DRAFT' || requirement.status === 'APPROVED');

    if (canReview) {
      return (
        <Button
          className={classes.actionButton}
          size="small"
          variant="outlined"
          color="primary"
          onClick={() => handleOpenInstanceReview(requirement)}
        >
          {t('testCaseGeneration.traceability.review')}
        </Button>
      );
    }

    return (
      <Typography variant="body2" color="textSecondary">
        —
      </Typography>
    );
  };

  return (
    <>
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {t('testCaseGeneration.traceability.dashboardTitle')}
          </Typography>

          <Typography color="textSecondary">
            {t('testCaseGeneration.traceability.dashboardDescription')}
          </Typography>

          <Box mt={2}>
            <Grid container spacing={1}>
              <Grid item xs={12} sm={6} md>
                <Card variant="outlined" className={classes.summaryCard}>
                  <CardContent
                    className={classes.summaryCardContent}
                    style={{ padding: 12 }}
                  >
                    <Typography variant="body2" color="textSecondary">
                      {t('testCaseGeneration.traceability.total')}
                    </Typography>

                    <Typography variant="h4">{totalRequirements}</Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6} md>
                <Card variant="outlined" className={classes.summaryCard}>
                  <CardContent
                    className={classes.summaryCardContent}
                    style={{ padding: 12 }}
                  >
                    <Typography variant="body2" color="textSecondary">
                      {t('testCaseGeneration.traceability.approvedTestCase')}
                    </Typography>

                    <Typography variant="h4">{approvedCount}</Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6} md>
                <Card variant="outlined" className={classes.summaryCard}>
                  <CardContent
                    className={classes.summaryCardContent}
                    style={{ padding: 12 }}
                  >
                    <Typography variant="body2" color="textSecondary">
                      {t('testCaseGeneration.traceability.draftTestCase')}
                    </Typography>

                    <Typography variant="h4">{draftCount}</Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6} md>
                <Card variant="outlined" className={classes.summaryCard}>
                  <CardContent
                    className={classes.summaryCardContent}
                    style={{ padding: 12 }}
                  >
                    <Typography variant="body2" color="textSecondary">
                      {t(
                        'testCaseGeneration.traceability.notCoveredRequirement',
                      )}
                    </Typography>

                    <Typography variant="h4">{notCoveredCount}</Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6} md>
                <Card variant="outlined" className={classes.coverageCard}>
                  <CardContent className={classes.summaryCardContent}>
                    <Typography variant="body2" color="textSecondary">
                      {t('testCaseGeneration.traceability.coverage')}
                    </Typography>

                    <Typography variant="h4" color="primary">
                      {coveragePercentage}%
                    </Typography>

                    <Typography variant="caption" color="textSecondary">
                      {t('testCaseGeneration.traceability.coverageSummary', {
                        covered: coveredCount,
                        total: totalRequirements,
                      })}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        </CardContent>
      </Card>

      <Box mt={3}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {t('testCaseGeneration.traceability.requirementTraceability')}
            </Typography>

            <Box className={classes.toolbar}>
              <TextField
                className={classes.searchField}
                size="small"
                variant="outlined"
                label={t('testCaseGeneration.traceability.searchPlaceholder')}
                value={searchText}
                onChange={event => setSearchText(event.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />

              <Box className={classes.toolbarActions}>
                <FormControl
                  size="small"
                  variant="outlined"
                  className={classes.exportFormat}
                >
                  <InputLabel>
                    {t('testCaseGeneration.export.format')}
                  </InputLabel>

                  <Select
                    value={traceabilityExportFormat}
                    label={t('testCaseGeneration.export.format')}
                    disabled={exportingTraceability}
                    onChange={event =>
                      setTraceabilityExportFormat(
                        event.target.value as ExportFormat,
                      )
                    }
                  >
                    <MenuItem value="markdown">
                      {t('testCaseGeneration.export.markdown')}
                    </MenuItem>

                    <MenuItem value="docx">
                      {t('testCaseGeneration.export.word')}
                    </MenuItem>

                    <MenuItem value="pdf">
                      {t('testCaseGeneration.export.pdf')}
                    </MenuItem>
                  </Select>
                </FormControl>

                <Button
                  color="primary"
                  variant="contained"
                  disabled={
                    selectedTraceabilityIds.size === 0 || exportingTraceability
                  }
                  onClick={handleTraceabilityExport}
                  startIcon={
                    exportingTraceability ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : undefined
                  }
                >
                  {t('testCaseGeneration.export.button')}
                </Button>
              </Box>
            </Box>

            {traceabilityExportValidationMessage && (
              <Box mb={2}>
                <Alert severity="warning">
                  {traceabilityExportValidationMessage}
                </Alert>
              </Box>
            )}

            {loading ? (
              <Box display="flex" justifyContent="center" p={4}>
                <CircularProgress size={24} />
              </Box>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell
                      padding="checkbox"
                      className={classes.tableHeader}
                    >
                      <Checkbox
                        checked={allExportableSelected}
                        indeterminate={
                          someExportableSelected && !allExportableSelected
                        }
                        disabled={exportableRequirements.length === 0}
                        onChange={event =>
                          handleToggleAllTraceability(event.target.checked)
                        }
                      />
                    </TableCell>
                    <TableCell className={classes.tableHeader}>
                      <strong>
                        {t('testCaseGeneration.traceability.requirementId')}
                      </strong>
                    </TableCell>

                    <TableCell className={classes.tableHeader}>
                      <strong>
                        {t('testCaseGeneration.traceability.requirementName')}
                      </strong>
                    </TableCell>

                    <TableCell className={classes.tableHeader}>
                      <strong>
                        {t('testCaseGeneration.traceability.requirementStatus')}
                      </strong>
                    </TableCell>

                    <TableCell className={classes.tableHeader}>
                      <strong>
                        {t('testCaseGeneration.traceability.testCase')}
                      </strong>
                    </TableCell>

                    <TableCell className={classes.tableHeader}>
                      <strong>
                        {t('testCaseGeneration.traceability.testCaseStatus')}
                      </strong>
                    </TableCell>

                    <TableCell className={classes.tableHeader}>
                      <strong>
                        {t('testCaseGeneration.traceability.action')}
                      </strong>
                    </TableCell>

                    <TableCell className={classes.tableHeader}>
                      <strong>
                        {t('testCaseGeneration.traceability.testCaseReview')}
                      </strong>
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {filteredRequirements.map(requirement => (
                    <TableRow key={requirement.id} hover>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={
                            Boolean(requirement.testCaseThreadId) &&
                            selectedTraceabilityIds.has(
                              requirement.testCaseThreadId as string,
                            )
                          }
                          disabled={
                            !requirement.testCaseId ||
                            !requirement.testCaseThreadId
                          }
                          onChange={() => {
                            if (requirement.testCaseThreadId) {
                              handleToggleTraceability(
                                requirement.testCaseThreadId,
                              );
                            }
                          }}
                        />
                      </TableCell>
                      <TableCell>{requirement.id}</TableCell>

                      <TableCell>{requirement.name}</TableCell>

                      <TableCell>
                        {requirement.requirementStatus || '-'}
                      </TableCell>

                      <TableCell>
                        {requirement.testCaseId ? (
                          <Link
                            component="button"
                            variant="body2"
                            disabled={!requirement.testCaseThreadId}
                            onClick={() =>
                              handleOpenVersionHistory(requirement)
                            }
                          >
                            {requirement.testCaseId}
                          </Link>
                        ) : (
                          '—'
                        )}
                      </TableCell>

                      <TableCell>
                        {renderStatusChip(requirement.status)}
                      </TableCell>

                      <TableCell>{renderActionButton(requirement)}</TableCell>

                      <TableCell className={classes.actionCell}>
                        {renderReviewAction(requirement)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </Box>

      <Dialog
        open={createRequirement !== null}
        onClose={handleCloseCreateForm}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>{t('testCaseGeneration.createForm.title')}</DialogTitle>

        <DialogContent>
          <Typography variant="subtitle2" gutterBottom>
            {t('testCaseGeneration.createForm.sourceRequirement')}
          </Typography>

          <Box
            mb={2}
            p={2}
            border={1}
            borderColor="divider"
            borderRadius={4}
            style={{
              whiteSpace: 'pre-wrap',
              maxHeight: 200,
              overflowY: 'auto',
            }}
          >
            <Typography variant="subtitle2" gutterBottom>
              {createRequirement
                ? `${createRequirement.id} — ${createRequirement.name}`
                : ''}
            </Typography>

            <Typography variant="body2">
              {createRequirementText || '—'}
            </Typography>
          </Box>

          {templateLoading && (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress size={24} />
            </Box>
          )}
          {!templateLoading &&
            editableTemplateFields.length === 0 &&
            !createError && (
              <Alert severity="info">
                {t('testCaseGeneration.createForm.noEditableFields')}
              </Alert>
            )}
          {!templateLoading &&
            editableTemplateFields.map(field => (
              <Box key={field.key} mb={2}>
                <TextField
                  fullWidth
                  variant="outlined"
                  size="small"
                  multiline
                  rows={field.isList ? 4 : 2}
                  required={field.required}
                  label={field.label}
                  value={createValues[field.key] ?? ''}
                  onChange={event =>
                    handleCreateFieldChange(field.key, event.target.value)
                  }
                  disabled={creating || templateLoading}
                  helperText={
                    field.isList
                      ? t('testCaseGeneration.detailView.onePerLine')
                      : undefined
                  }
                />
              </Box>
            ))}

          {createError && (
            <Box mt={2}>
              <Alert severity="error">{createError}</Alert>
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          <Button
            onClick={handleCloseCreateForm}
            disabled={creating || templateLoading}
          >
            {t('testCaseGeneration.createForm.cancel')}
          </Button>
          <Button
            color="primary"
            variant="contained"
            onClick={handleSubmitCreateForm}
            disabled={
              templateLoading ||
              editableTemplateFields.length === 0 ||
              !hasCreateInput ||
              !requiredCreateFieldsComplete ||
              creating
            }
            startIcon={creating ? <CircularProgress size={16} /> : undefined}
          >
            {creating
              ? t('testCaseGeneration.createForm.creating')
              : t('testCaseGeneration.createForm.submit')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={detailOpen}
        onClose={handleCloseDetail}
        fullWidth
        maxWidth="lg"
      >
        <DialogTitle>{t('testCaseGeneration.detailView.title')}</DialogTitle>

        <DialogContent>
          {detailLoading && (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress size={24} />
            </Box>
          )}

          {!detailLoading && detailError && (
            <Alert severity="error">{detailError}</Alert>
          )}

          {!detailLoading && !detailError && detailThread && (
            <>
              <Box
                display="flex"
                alignItems="center"
                gridGap={16}
                flexWrap="wrap"
                mb={2}
              >
                <Chip
                  size="small"
                  color={
                    detailThread.status === 'APPROVED' ? 'primary' : 'default'
                  }
                  label={t(
                    `testCaseGeneration.detailView.status.${detailThread.status}`,
                  )}
                />
                <Typography variant="body2" color="textSecondary">
                  {t('testCaseGeneration.detailView.version', {
                    version: detailThread.currentVersionNumber,
                  })}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {t('testCaseGeneration.detailView.lastEdited', {
                    time: detailThread.updatedAt
                      ? new Date(detailThread.updatedAt).toLocaleString()
                      : '—',
                  })}
                </Typography>
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={12} md={5}>
                  <Typography variant="subtitle2" gutterBottom>
                    {t('testCaseGeneration.createForm.sourceRequirement')}
                  </Typography>
                  <Box
                    p={2}
                    border={1}
                    borderColor="divider"
                    borderRadius={4}
                    style={{
                      whiteSpace: 'pre-wrap',
                      maxHeight: 320,
                      overflowY: 'auto',
                    }}
                  >
                    <Typography variant="body2">
                      {detailRequirement
                        ? `${detailRequirement.id} — ${detailRequirement.name}`
                        : '—'}
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} md={7}>
                  <Box
                    style={{ maxHeight: 440, overflowY: 'auto', paddingTop: 8 }}
                  >
                    {currentFields.map(field => {
                      const isIdField = isTestCaseIdFieldKey(field.key);
                      const isListField = Array.isArray(field.value);
                      const fieldRows = isListField ? 4 : 2;

                      return (
                        <Box key={field.key} mb={2}>
                          <TextField
                            fullWidth
                            variant="outlined"
                            size="small"
                            label={field.label}
                            multiline={!isIdField}
                            rows={isIdField ? undefined : fieldRows}
                            value={editValues[field.key] ?? ''}
                            onChange={event =>
                              handleFieldChange(field.key, event.target.value)
                            }
                            disabled={
                              isIdField ||
                              detailThread.status !== 'WIP' ||
                              detailBusy
                            }
                            helperText={
                              isListField && detailThread.status === 'WIP'
                                ? t('testCaseGeneration.detailView.onePerLine')
                                : undefined
                            }
                          />
                        </Box>
                      );
                    })}
                  </Box>
                </Grid>
              </Grid>

              {detailThread.status === 'DRAFT' && (
                <Box mt={2}>
                  <Alert severity="info">
                    {t('testCaseGeneration.detailView.draftHint')}
                  </Alert>
                </Box>
              )}

              {detailActionError && (
                <Box mt={2}>
                  <Alert severity="error">{detailActionError}</Alert>
                </Box>
              )}
            </>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCloseDetail} disabled={detailBusy}>
            {t('testCaseGeneration.detailView.close')}
          </Button>

          {detailThread?.status === 'DRAFT' && (
            <Button
              color="primary"
              variant="contained"
              onClick={handleStartWorking}
              disabled={detailBusy}
              startIcon={
                detailBusy ? <CircularProgress size={16} /> : undefined
              }
            >
              {t('testCaseGeneration.detailView.startWorking')}
            </Button>
          )}

          {detailThread?.status === 'WIP' && (
            <Button
              color="primary"
              variant="contained"
              onClick={handleSaveEdit}
              disabled={!isDirty || detailBusy}
              startIcon={
                detailBusy ? <CircularProgress size={16} /> : undefined
              }
            >
              {t('testCaseGeneration.detailView.save')}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <TestCaseVersionHistoryPanel
        open={selectedHistoryRequirement !== null}
        testCaseId={selectedHistoryRequirement?.testCaseId ?? null}
        testCaseThreadId={selectedHistoryRequirement?.testCaseThreadId ?? null}
        refreshKey={historyRefreshKey}
        showFeedbackAction={
          Boolean(
            selectedHistoryRequirement?.testCaseId &&
              selectedHistoryRequirement?.testCaseThreadId,
          ) && selectedHistoryRequirement?.status !== 'APPROVED'
        }
        onFeedback={() => {
          if (
            selectedHistoryRequirement?.testCaseId &&
            selectedHistoryRequirement.testCaseThreadId
          ) {
            setTraceabilityFeedbackOpen(true);
          }
        }}
        onClose={() => {
          setSelectedHistoryRequirement(null);
          setTraceabilityFeedbackOpen(false);
        }}
      />

      <TestCaseFeedbackDialog
        open={traceabilityFeedbackOpen}
        submitting={submittingTraceabilityFeedback}
        appliesToCurrentVersion
        onClose={() => setTraceabilityFeedbackOpen(false)}
        onSubmit={handleSubmitTraceabilityFeedback}
      />

      <InstanceReviewPanel
        open={instanceReviewOpen}
        testCaseThreadId={selectedReviewRequirement?.testCaseThreadId ?? null}
        testCaseId={selectedReviewRequirement?.testCaseId ?? null}
        testCaseTitle={selectedReviewRequirement?.testCaseTitle}
        requirementId={selectedReviewRequirement?.id}
        requirementName={selectedReviewRequirement?.name}
        initialThreadStatus={
          selectedReviewRequirement?.testCaseThreadStatus ??
          (selectedReviewRequirement?.status === 'APPROVED'
            ? 'APPROVED'
            : 'DRAFT')
        }
        onThreadStatusChange={(_status: TestCaseThreadStatus) => {
          void loadTraceability();
        }}
        onDeleted={() => {
          setInstanceReviewOpen(false);
          setSelectedReviewRequirement(null);
          void loadTraceability();
        }}
        onClose={() => {
          setInstanceReviewOpen(false);
          setSelectedReviewRequirement(null);
        }}
        onRefresh={async () => {
          await loadTraceability();
        }}
      />

      <Snackbar
        open={traceabilityFeedbackSuccessOpen}
        autoHideDuration={4000}
        onClose={() => setTraceabilityFeedbackSuccessOpen(false)}
      >
        <Alert
          severity="success"
          onClose={() => setTraceabilityFeedbackSuccessOpen(false)}
        >
          {t('testCaseGeneration.feedback.success')}
        </Alert>
      </Snackbar>

      <Snackbar
        open={traceabilityFeedbackErrorMessage !== null}
        autoHideDuration={5000}
        onClose={() => setTraceabilityFeedbackErrorMessage(null)}
      >
        <Alert
          severity="error"
          onClose={() => setTraceabilityFeedbackErrorMessage(null)}
        >
          {traceabilityFeedbackErrorMessage}
        </Alert>
      </Snackbar>
      <Snackbar
        open={generateSuccessOpen}
        autoHideDuration={4000}
        onClose={() => setGenerateSuccessOpen(false)}
      >
        <Alert severity="success" onClose={() => setGenerateSuccessOpen(false)}>
          {t('testCaseGeneration.traceability.generateSuccess')}
        </Alert>
      </Snackbar>

      <Snackbar
        open={generateErrorOpen}
        autoHideDuration={5000}
        onClose={() => setGenerateErrorOpen(false)}
      >
        <Alert severity="error" onClose={() => setGenerateErrorOpen(false)}>
          {t('testCaseGeneration.traceability.generateFailed')}
        </Alert>
      </Snackbar>

      <Snackbar
        open={traceabilityExportSuccessOpen}
        autoHideDuration={4000}
        onClose={() => setTraceabilityExportSuccessOpen(false)}
      >
        <Alert
          severity="success"
          onClose={() => setTraceabilityExportSuccessOpen(false)}
        >
          {t('testCaseGeneration.export.success')}
        </Alert>
      </Snackbar>

      <Snackbar
        open={traceabilityExportErrorOpen}
        autoHideDuration={5000}
        onClose={() => setTraceabilityExportErrorOpen(false)}
      >
        <Alert
          severity="error"
          onClose={() => setTraceabilityExportErrorOpen(false)}
        >
          {t('testCaseGeneration.export.failed')}
        </Alert>
      </Snackbar>
    </>
  );
};

export default TraceabilityDashboard;
